/**
 * Word-level diff for version history (PROJECT VERSIONING).
 *
 * AGNOSTIC piece (guide VERSION_HISTORY): knows nothing about the domain; it
 * compares two texts and returns `equal | added | removed` segments ready to
 * paint (added = present in the new text, removed = only in the old one).
 *
 * Algorithm: classic LCS by dynamic programming over tokens (words +
 * separators). O(n·m) — enough for short billing-definition texts. Safeguard:
 * if n·m exceeds a cap, the common prefix/suffix is trimmed and the rest is
 * marked as a full replacement (never hangs the UI).
 */

export type DiffSegmentType = "equal" | "added" | "removed";

export interface DiffSegment {
  type: DiffSegmentType;
  text: string;
}

/** DP work cap (old_tokens × new_tokens). */
const MAX_DP_CELLS = 1_000_000;

/** Splits into words and separators (whitespace/newlines are kept). */
export function tokenizeWords(text: string): string[] {
  if (!text) {
    return [];
  }
  return text.split(/(\s+)/u).filter((t) => t.length > 0);
}

function pushSegment(out: DiffSegment[], type: DiffSegmentType, text: string) {
  if (!text) {
    return;
  }
  const last = out.at(-1);
  if (last && last.type === type) {
    last.text += text;
  } else {
    out.push({ type, text });
  }
}

/**
 * Word-by-word diff between `oldText` (version) and `newText` (current).
 * Returns segments in reading order of the NEW text, with removed segments
 * interleaved where they were.
 */
export function diffWords(oldText: string, newText: string): DiffSegment[] {
  if (oldText === newText) {
    return oldText ? [{ type: "equal", text: oldText }] : [];
  }

  let a = tokenizeWords(oldText);
  let b = tokenizeWords(newText);
  const out: DiffSegment[] = [];

  // Common prefix/suffix trim: cheapens the DP and is the common case when
  // editing (a field changes, the rest is identical).
  let prefixLength = 0;
  while (
    prefixLength < a.length &&
    prefixLength < b.length &&
    a[prefixLength] === b[prefixLength]
  ) {
    prefixLength += 1;
  }
  let suffixLength = 0;
  while (
    suffixLength < a.length - prefixLength &&
    suffixLength < b.length - prefixLength &&
    a[a.length - 1 - suffixLength] === b[b.length - 1 - suffixLength]
  ) {
    suffixLength += 1;
  }
  const prefix = a.slice(0, prefixLength).join("");
  const suffix = suffixLength > 0 ? a.slice(a.length - suffixLength).join("") : "";
  a = a.slice(prefixLength, a.length - suffixLength);
  b = b.slice(prefixLength, b.length - suffixLength);

  pushSegment(out, "equal", prefix);

  if (a.length * b.length > MAX_DP_CELLS) {
    // Oversized text: block replacement (correct, if coarser).
    pushSegment(out, "removed", a.join(""));
    pushSegment(out, "added", b.join(""));
    pushSegment(out, "equal", suffix);
    return out;
  }

  // LCS by DP: dp[i][j] = length of the LCS of a[i..] and b[j..].
  const n = a.length;
  const m = b.length;
  const width = m + 1;
  const dp = new Uint32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i * width + j] =
        a[i] === b[j]
          ? (dp[(i + 1) * width + j + 1] ?? 0) + 1
          : Math.max(dp[(i + 1) * width + j] ?? 0, dp[i * width + j + 1] ?? 0);
    }
  }

  // Reconstruction: emit removed before added at each divergence.
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    const tokenA = a[i] ?? "";
    const tokenB = b[j] ?? "";
    if (tokenA === tokenB) {
      pushSegment(out, "equal", tokenA);
      i += 1;
      j += 1;
    } else if ((dp[(i + 1) * width + j] ?? 0) >= (dp[i * width + j + 1] ?? 0)) {
      pushSegment(out, "removed", tokenA);
      i += 1;
    } else {
      pushSegment(out, "added", tokenB);
      j += 1;
    }
  }
  while (i < n) {
    pushSegment(out, "removed", a[i] ?? "");
    i += 1;
  }
  while (j < m) {
    pushSegment(out, "added", b[j] ?? "");
    j += 1;
  }

  pushSegment(out, "equal", suffix);
  return out;
}

/** Diff summary: words (not separators) added and removed. */
export function diffStats(segments: DiffSegment[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const segment of segments) {
    if (segment.type === "equal") {
      continue;
    }
    const words = segment.text.split(/\s+/u).filter((w) => w.length > 0).length;
    if (segment.type === "added") {
      added += words;
    } else {
      removed += words;
    }
  }
  return { added, removed };
}
